import * as echarts from "echarts";
import { IConnectRender } from "../../definitions/IConnectRender";

/** Contador para ids únicos por instancia. */
let ECHART_ID = 0;

/** Base para gráficos ECharts (Render/Connect): monta un `<div>`, inicializa
 *  la instancia en `connect()` con `getOption()` (definido por cada gráfico
 *  concreto) y la libera en `dispose()`. Se redimensiona con su contenedor
 *  (`ResizeObserver`), no con el resize de la ventana, porque el contenedor
 *  puede cambiar de tamaño sin que la ventana lo haga. */
export abstract class EChartRender implements IConnectRender {
  private readonly _chartId: number;
  private _chart: echarts.ECharts | null = null;
  private _resizeObserver: ResizeObserver | null = null;

  constructor() {
    ECHART_ID += 1;
    this._chartId = ECHART_ID;
  }

  render(): string {
    return `<div id="echart-${this._chartId}" class="sx-echart"></div>`;
  }

  /** Inicializa ECharts sobre el `<div>` de `render()` y observa su tamaño. */
  connect(): void {
    const el = document.getElementById(`echart-${this._chartId}`);
    if (!el) return;
    this._chart = echarts.init(el);
    this._chart.setOption(this.getOption());
    this._resizeObserver = new ResizeObserver(() => this._chart?.resize());
    this._resizeObserver.observe(el);
  }

  dispose(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._chart?.dispose();
    this._chart = null;
  }

  /** Vuelve a aplicar `getOption()` sobre la instancia viva. Para gráficos
   *  interactivos, cuyas series dependen de un estado que cambia después de
   *  `connect()` (p. ej. los filtros de lote/plaga del dashboard de
   *  monitoreo). Usa `notMerge: true`: al deseleccionar una serie hay que
   *  reemplazar el arreglo entero, no fusionarlo — si no, las series viejas
   *  sobrevivirían al merge. No-op si el gráfico todavía no conectó o ya se
   *  dispuso. */
  protected refresh(): void {
    this._chart?.setOption(this.getOption(), true);
  }

  /** Opciones del gráfico (series, ejes, tooltip, leyenda…), definidas por
   *  cada gráfico concreto. */
  protected abstract getOption(): echarts.EChartsOption;
}
